import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, CloseButton, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiVideo } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { FaCartPlus, FaShoppingCart } from "react-icons/fa";
import { getContrastColor, fetchColors } from "../utils/colorUtils";
import { useContext } from "react";
import { CartContext } from "../context/CartContext";
import Swal from "sweetalert2";

const ProductDetailModal = ({
  show,
  onHide,
  product,
  onAddToCart,
  isInCart,
  loadingItems,
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [selectedImage, setSelectedImage] = useState(
    (product?.images && product.images.length > 0)
      ? (Array.isArray(product.images) ? product.images[0] : product.images)
      : product?.image || ""
  );
  const [showStockAlert, setShowStockAlert] = useState(false);
  const storedUser = localStorage.getItem("token");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [colorError, setColorError] = useState(false);
  const [availableColorStocks, setAvailableColorStocks] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [availableColors, setAvailableColors] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [stockAlertMessage, setStockAlertMessage] = useState("");
  // State for selected capacity variant - default to null (no selection)
  const [selectedCapacity, setSelectedCapacity] = useState(null);
  const { cart } = useContext(CartContext);
  const [selectedBottleVariantIndex, setSelectedBottleVariantIndex] = useState(0);
  const [selectedBottleVariant, setSelectedBottleVariant] = useState(null);
  const [validationError, setValidationError] = useState("");


  const navigate = useNavigate();

  // Helper function for YouTube embed
  const getEmbedUrl = (url) => {
    if (!url) return "";

    const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
    if (shortsMatch && shortsMatch[1]) {
      return `https://www.youtube.com/embed/${shortsMatch[1]}?autoplay=1`;
    }

    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|watch\?.+&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}?autoplay=1`;
    }

    return url;
  };

  // Debug: Log selected capacity and cart status
  useEffect(() => {
    if (selectedCapacity) {
      const capacityData = getSelectedCapacityData();
      const isInCart = isSelectedVariantInCart();
      console.log('Selected Capacity:', {
        selectedCapacity,
        capacityData,
        isInCart,
        cartItems: cart.map(item => ({
          productId: item.product?._id,
          capacity: item.product?.selectedCapacity?.name || item.product?.selectedCapacity?.capacity,
          color: item.product?.selectedColor?.id
        }))
      });
    }
  }, [selectedCapacity, cart, product, selectedColor]);

  // Check if the product selection is valid for adding to cart
  const isSelectionValid = () => {
    // If product has bottle variants with capacity options
    if (product?.bottleVariants && product.bottleVariants.length > 0) {
      const currentVariant = product.bottleVariants[selectedBottleVariantIndex];
      // If the selected bottle variant has capacity options
      if (currentVariant?.capacityVariants && currentVariant.capacityVariants.length > 0) {
        // User MUST select a capacity
        if (!selectedCapacity) {
          return false;
        }
      }
    }

    // If product has capacity variants directly (no bottle variants)
    if (product?.capacityVariants && product.capacityVariants.length > 0 && !product?.bottleVariants?.length) {
      if (!selectedCapacity) {
        return false;
      }
    }

    // If product has colors, user must select a color
    if (availableColorStocks.length > 0 && !selectedColor) {
      return false;
    }

    return true;
  };

  const getBottleVariantData = () => {
    if (!product?.bottleVariants || product.bottleVariants.length === 0) {
      return null;
    }

    const index = selectedBottleVariantIndex || 0;
    const variant = product.bottleVariants[index];

    if (!variant) return null;

    // Check if the bottle variant has meaningful data
    // If all fields are empty/zero, treat it as null
    const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
    const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
    const hasStock = variant.stock && parseFloat(variant.stock) > 0;

    // Check if there are any capacity variants with data
    const hasValidCapacityVariants = variant.capacityVariants &&
      variant.capacityVariants.some(c =>
        c.capacity && c.capacity.trim() !== '' &&
        c.price && parseFloat(c.price) > 0
      );

    // If no meaningful data, return null (fallback to main product)
    if (!hasBottleSize && !hasSellingPrice && !hasStock && !hasValidCapacityVariants) {
      return null;
    }

    // Calculate final price after discount
    const sellingPrice = parseFloat(variant.sellingPrice) || 0;
    const discountValue = parseFloat(variant.discountValue) || 0;
    let finalPrice = sellingPrice;

    if (discountValue > 0) {
      if (variant.discountType === 'percentage') {
        finalPrice = sellingPrice - (sellingPrice * discountValue / 100);
      } else {
        finalPrice = sellingPrice - discountValue;
      }
    }

    // For price, if selling price is 0 but product has price, use product price
    const effectivePrice = sellingPrice > 0 ? sellingPrice : (product?.price || 0);
    const effectiveFinalPrice = finalPrice > 0 ? finalPrice : (product?.price || 0);

    return {
      variant,
      index,
      finalPrice: effectiveFinalPrice,
      sellingPrice: effectivePrice,
      totalVariants: product.bottleVariants.length,
      hasDiscount: discountValue > 0 && sellingPrice > 0,
      discountValue,
      discountType: variant.discountType
    };
  };

  // Handle bottle variant change
  const handleBottleVariantChange = (index) => {
    setSelectedBottleVariantIndex(index);
    // Reset selected capacity when changing bottle variant
    setSelectedCapacity(null);
    setQuantity(1);
  };

  // Get current price including bottle variant
  const getCurrentPriceWithBottle = () => {
    const capacityData = getSelectedCapacityData();

    // If capacity variant is selected, return its price
    if (capacityData && capacityData.price) {
      return capacityData.price;
    }

    // If bottle variant is selected, use its final price
    const bottleData = getBottleVariantData();
    if (bottleData) {
      return bottleData.finalPrice || bottleData.sellingPrice || 0;
    }

    // Fallback to main product price with discount
    if (product?.discount > 0) {
      return product.price * (1 - product.discount / 100);
    }

    return product?.price || 0;
  };

  // Get current stock with bottle variant - FALLBACK to main product stock
  const getCurrentStockWithBottle = () => {
    const bottleData = getBottleVariantData();
    const capacityData = getSelectedCapacityData();

    // 1. If capacity is selected and has stock, use it
    if (capacityData && capacityData.stock !== undefined) {
      return capacityData.stock;
    }

    // 2. If bottle variant has stock, use it
    if (bottleData && bottleData.variant.stock > 0) {
      return bottleData.variant.stock;
    }

    // 3. FALLBACK: Use main product stock if available
    // This handles the case where bottle variant has 0 stock but main product has stock
    if (product?.Item > 0) {
      return product.Item;
    }

    // 4. Final fallback
    return 0;
  };

  const showAlreadyInCartAlert = () => {
    const capacityData = getSelectedCapacityData();
    const capacityName = capacityData?.name;

    const cartItem = cart.find(item => {
      const cartProduct = item.product;
      if (cartProduct?._id !== product?._id) return false;

      // Check color match (only if color is selected)
      if (selectedColor) {
        if (cartProduct?.selectedColor?.id !== selectedColor) return false;
      } else {
        if (cartProduct?.selectedColor) return false;
      }

      // Check by capacity name
      const cartCapacityName = cartProduct?.selectedCapacity?.name ||
        cartProduct?.selectedCapacity?.capacity;
      return cartCapacityName === capacityName;
    });

    // Build the full variant name
    let variantParts = [];
    if (cartItem?.product?.selectedCapacity) {
      variantParts.push(cartItem.product.selectedCapacity.name ||
        cartItem.product.selectedCapacity.capacity);
    }
    if (cartItem?.product?.selectedBottleVariant) {
      variantParts.push(`${cartItem.product.selectedBottleVariant.bottleSize}ml`);
    }

    const variantName = variantParts.length > 0 ? ` (${variantParts.join(' - ')})` : '';
    const fullProductName = `${product.title}${variantName}`;

    Swal.fire({
      icon: 'info',
      title: t('already_in_cart'),
      text: fullProductName,
      confirmButtonColor: '#3498db',
      confirmButtonText: t('ok'),
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // Helper function to get image for selected color based on index
  const getImageForColor = (colorId) => {
    if (!product?.images || !Array.isArray(product.images)) {
      return product?.image || "";
    }

    const colorIndex = availableColorStocks.findIndex(c => c.id === colorId);

    if (colorIndex >= 0 && product.images[colorIndex]) {
      return product.images[colorIndex];
    }

    return product.images[0] || product?.image || "";
  };

  const isSelectedColorInCart = () => {
    if (!selectedColor) return false;

    // Get the selected capacity data
    const capacityData = getSelectedCapacityData();
    const capacityId = capacityData?._id || null;

    // For bottle variants without capacity selection, check bottle size
    const bottleData = getBottleVariantData();
    const bottleSize = bottleData?.variant?.bottleSize || null;

    return cart.some(cartItem => {
      const cartProduct = cartItem.product;

      // Check if it's the same product
      if (cartProduct?._id !== product?._id) return false;

      // Check if it's the same color
      if (cartProduct?.selectedColor?.id !== selectedColor) return false;

      // Check if it's the same capacity (if capacity is selected)
      if (capacityId) {
        return cartProduct?.selectedCapacity?._id === capacityId;
      }

      // Check if it's the same bottle variant (if bottle is selected but no capacity)
      if (bottleSize) {
        return cartProduct?.selectedBottleVariant?.bottleSize === bottleSize;
      }

      // If no capacity and no bottle selected, it's the same product
      return true;
    });
  };

  const isImageOutOfStock = (index) => {
    if (!availableColorStocks || availableColorStocks.length === 0) {
      return !product?.Item || product.Item <= 0;
    }

    const colorAtIndex = availableColorStocks[index];
    return !colorAtIndex || colorAtIndex.quantity <= 0;
  };

  // Update getMaxAvailableQuantity to consider fallback
  const getMaxAvailableQuantity = () => {
    const capacityData = getSelectedCapacityData();

    // 1. If capacity is selected, use its stock
    if (capacityData && capacityData.stock !== undefined) {
      return capacityData.stock;
    }

    // 2. If no capacity selected but bottle variant exists, use bottle stock or fallback
    const bottleData = getBottleVariantData();
    if (bottleData) {
      // Use bottle stock if available, otherwise fallback to main product stock
      return (bottleData.variant.stock > 0) ? bottleData.variant.stock : (product?.Item || 0);
    }

    // 3. Check color stock if available
    if (availableColorStocks.length > 0 && selectedColor) {
      const selected = availableColorStocks.find(c => c.id === selectedColor);
      return selected?.quantity ?? 0;
    }

    // 4. Fallback to product stock
    return product?.Item ?? 0;
  };

  // Get selected capacity data - returns null if no capacity selected
  const getSelectedCapacityData = () => {
    if (!selectedCapacity) {
      return null;
    }

    // Check if it's a temp selection (from bottle variant)
    if (typeof selectedCapacity === 'string' && selectedCapacity.startsWith('temp_')) {
      const tempName = selectedCapacity.replace('temp_', '');
      const bottleData = getBottleVariantData();
      const capItem = bottleData?.variant.capacityVariants?.find(c => c.capacity === tempName);
      if (capItem) {
        return {
          _id: selectedCapacity,
          name: capItem.capacity,
          price: capItem.price,
          stock: 999,
          isTemp: true
        };
      }
      return null;
    }

    // Check main capacity variants by _id
    return product?.capacityVariants?.find(v => v._id === selectedCapacity) || null;
  };

  // Get current price - falls back to product price
  const getCurrentPrice = () => {
    const capacityData = getSelectedCapacityData();

    // 1. If a capacity variant is selected, return its price
    if (capacityData && capacityData.price) {
      return capacityData.price;
    }

    // 2. If no capacity selected but bottle variant exists with valid data
    const bottleData = getBottleVariantData();
    if (bottleData && bottleData.finalPrice > 0) {
      return bottleData.finalPrice;
    }

    // 3. Fallback to main product price with discount
    if (product?.discount > 0) {
      return product.price * (1 - product.discount / 100);
    }

    return product?.price || 0;
  };

  // Get current stock - uses product Item if no capacity selected
  const getCurrentStock = () => {
    const capacityData = getSelectedCapacityData();

    // 1. If capacity is selected, use its stock
    if (capacityData && capacityData.stock !== undefined) {
      return capacityData.stock;
    }

    // 2. If no capacity selected but bottle variant exists, check bottle stock
    const bottleData = getBottleVariantData();
    if (bottleData) {
      // Use bottle stock if available, otherwise fallback to main product stock
      return (bottleData.variant.stock > 0) ? bottleData.variant.stock : (product?.Item || 0);
    }

    // 3. Fallback to product stock
    return product?.Item || 0;
  };

  const syncColorByImageIndex = (imageIndex) => {
    if (availableColorStocks.length === 0) {
      return;
    }

    const colorAtIndex = availableColorStocks[imageIndex];
    if (colorAtIndex && colorAtIndex.quantity > 0) {
      setSelectedColor(colorAtIndex.id);
      setColorError(false);
    }
  };

  useEffect(() => {
    const fetchAndProcessColors = async () => {
      try {
        const allColors = await fetchColors();

        if (allColors.length > 0) {
          setAvailableColors(allColors);

          if (product?.ProductColor && Array.isArray(product.ProductColor) && product.ProductColor.length > 0) {
            const productColors = product.ProductColor.map((colorId, index) => {
              const colorObj = allColors.find(c => c._id === colorId);
              const colorStock = product.colorStocks?.find(stock =>
                stock.color === colorId || stock.color === colorObj?.name
              );

              return {
                id: colorId,
                name: colorObj?.name || colorId,
                hexCode: colorObj?.hexCode || "#cccccc",
                quantity: colorStock?.quantity ?? 0,
                index: index
              };
            }).filter(color => color);

            setAvailableColorStocks(productColors);

            if (productColors.length > 0 && !selectedColor) {
              const firstAvailableColor = productColors.find(color => (color.quantity || 0) > 0);
              if (firstAvailableColor) {
                setSelectedColor(firstAvailableColor.id);
                const initialImage = getImageForColor(firstAvailableColor.id);
                if (initialImage) {
                  setSelectedImage(initialImage);
                }
              }
            }
          } else {
            setAvailableColorStocks([]);
            setSelectedColor("");
          }
        }
      } catch (error) {
        console.error("Error fetching colors:", error);
        setAvailableColorStocks([]);
        setSelectedColor("");
      }
    };
    if (show && product) {
      fetchAndProcessColors();
      // Set selectedCapacity to null (no selection by default)
      setSelectedCapacity(null);
      // Set default bottle variant to first one if available
      if (product.bottleVariants && product.bottleVariants.length > 0) {
        setSelectedBottleVariantIndex(0);
      } else {
        setSelectedBottleVariantIndex(0);
      }
      setQuantity(1);
    }
    if (show && product) {
      fetchAndProcessColors();
      // Set selectedCapacity to null (no selection by default)
      setSelectedCapacity(null);
      setQuantity(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, product]);

  useEffect(() => {
    const maxQty = getMaxAvailableQuantity();
    if (quantity > maxQty && maxQty > 0) {
      setQuantity(maxQty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, availableColorStocks, selectedCapacity]);

  const calculateTotalPrice = () => {
    const currentPrice = getCurrentPrice();
    return (currentPrice * quantity).toFixed(2);
  };

  // MODIFIED: Handle capacity selection - toggle on/off
  const handleCapacitySelect = (capacityId) => {
    // If clicking the same capacity, deselect it (set to null)
    if (selectedCapacity === capacityId) {
      setSelectedCapacity(null);
      setQuantity(1);
    } else {
      // For bottle variants, we use temp_ prefix
      setSelectedCapacity(capacityId);
      setQuantity(1);
    }
  };

  // Replace the isSelectedVariantInCart function with this:

  const isSelectedVariantInCart = () => {
    // Get the selected capacity data
    const capacityData = getSelectedCapacityData();
    const capacityName = capacityData?.name || null;

    // Get bottle variant data
    const bottleData = getBottleVariantData();
    const bottleSize = bottleData?.variant?.bottleSize || null;

    // If no capacity selected AND no bottle size selected, return false
    if (!capacityName && !bottleSize) return false;

    console.log('🔍 isSelectedVariantInCart - Current selection:', {
      capacityName,
      bottleSize,
      selectedColor,
      selectedBottleVariantIndex,
      hasBottleData: !!bottleData,
      bottleData: bottleData?.variant
    });

    console.log('📦 Cart items:', cart.map((item, idx) => ({
      index: idx,
      id: item.product._id,
      title: item.product.title,
      capacity: item.product?.selectedCapacity?.name || item.product?.selectedCapacity?.capacity,
      capacityId: item.product?.selectedCapacity?._id,
      bottleSize: item.product?.selectedBottleVariant?.bottleSize,
      hasBottleVariant: !!item.product?.selectedBottleVariant,
      color: item.product?.selectedColor?.id,
      quantity: item.quantity
    })));

    const result = cart.some(cartItem => {
      const cartProduct = cartItem.product;

      // Check if it's the same product
      if (cartProduct?._id !== product?._id) {
        console.log(`  ⏭️ Product ID mismatch: ${cartProduct?._id} !== ${product?._id}`);
        return false;
      }

      // Check color match
      if (selectedColor && cartProduct?.selectedColor?.id !== selectedColor) {
        console.log(`  ⏭️ Color mismatch: ${cartProduct?.selectedColor?.id} !== ${selectedColor}`);
        return false;
      }
      if (!selectedColor && cartProduct?.selectedColor) {
        console.log(`  ⏭️ Cart has color but current doesn't: ${cartProduct?.selectedColor?.id}`);
        return false;
      }

      // Get cart item's capacity name
      const cartCapacityName = cartProduct?.selectedCapacity?.name ||
        cartProduct?.selectedCapacity?.capacity;

      // Get cart item's bottle size
      const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;

      console.log(`  📊 Comparing: capacity=${capacityName}, bottleSize=${bottleSize} vs cart capacity=${cartCapacityName}, cart bottle=${cartBottleSize}`);

      // If capacity is selected, it must match AND bottle size must match
      if (capacityName) {
        // Capacity name must match
        if (cartCapacityName !== capacityName) {
          console.log(`  ❌ Capacity mismatch: ${cartCapacityName} !== ${capacityName}`);
          return false;
        }

        // Bottle size must match (or both null)
        if (bottleSize && cartBottleSize !== bottleSize) {
          console.log(`  ❌ Bottle size mismatch: ${cartBottleSize} !== ${bottleSize}`);
          return false;
        }
        if (!bottleSize && cartBottleSize) {
          console.log(`  ❌ Cart has bottle size but current doesn't: ${cartBottleSize} !== null`);
          return false;
        }

        console.log(`  ✅ MATCH FOUND! Capacity: ${capacityName}, Bottle: ${bottleSize}`);
        return true;
      }

      // If no capacity selected but bottle size is selected (full box)
      if (bottleSize) {
        // No capacity in cart item
        if (cartCapacityName) {
          console.log(`  ❌ Cart has capacity but current doesn't: ${cartCapacityName} !== null`);
          return false;
        }
        // Bottle size must match
        if (cartBottleSize !== bottleSize) {
          console.log(`  ❌ Bottle size mismatch: ${cartBottleSize} !== ${bottleSize}`);
          return false;
        }
        console.log(`  ✅ MATCH FOUND! Full box: ${bottleSize}`);
        return true;
      }

      console.log(`  ⏭️ No match conditions met`);
      return false;
    });

    console.log(`🔍 Final result: ${result}`);
    return result;
  };
  // Replace the getVariantInCartName function with this:

  const getVariantInCartName = () => {
    const capacityData = getSelectedCapacityData();
    const capacityName = capacityData?.name || null;
    const bottleData = getBottleVariantData();
    const bottleSize = bottleData?.variant?.bottleSize || null;

    const cartItem = cart.find(item => {
      const cartProduct = item.product;
      if (cartProduct?._id !== product?._id) return false;
      if (selectedColor && cartProduct?.selectedColor?.id !== selectedColor) return false;
      if (!selectedColor && cartProduct?.selectedColor) return false;

      const cartCapacityName = cartProduct?.selectedCapacity?.name ||
        cartProduct?.selectedCapacity?.capacity;
      const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;

      // Match by capacity name AND bottle size
      if (capacityName && bottleSize) {
        return cartCapacityName === capacityName && cartBottleSize === bottleSize;
      }
      if (capacityName && !bottleSize) {
        return cartCapacityName === capacityName && !cartBottleSize;
      }
      if (!capacityName && bottleSize) {
        return !cartCapacityName && cartBottleSize === bottleSize;
      }
      return false;
    });

    if (cartItem?.product?.selectedCapacity) {
      return cartItem.product.selectedCapacity.name;
    }
    if (cartItem?.product?.selectedBottleVariant) {
      return `${cartItem.product.selectedBottleVariant.bottleSize}ml`;
    }
    return '';
  };

  const handleBuyNow = () => {
    if (!storedUser) {
      setShowLoginModal(true);
      return;
    }

    const capacityData = getSelectedCapacityData();
    const currentPrice = getCurrentPrice();
    const currentStock = getCurrentStock();

    const completeProduct = {
      ...product,
      quantity: quantity,
      price: currentPrice, // Use the discounted price if no variant, or variant price
      Item: currentStock,
      selectedCapacity: capacityData,
      displayTitle: capacityData
        ? `${product.title} (${capacityData.name})`
        : product.title
    };

    navigate("/checkout", {
      state: {
        cart: [
          {
            product: completeProduct,
            quantity: quantity,
          },
        ],
        total: calculateTotalPrice(),
        buyNow: true,
      },
    });
    onHide();
  };

  const handleIncreaseQuantity = () => {
    const maxQty = getCurrentStock();

    if (quantity < maxQty) {
      setQuantity(prev => prev + 1);
      setShowStockAlert(false);
      setStockAlertMessage("");
    } else {
      let message = "";

      if (availableColorStocks.length > 0 && selectedColor) {
        const selectedColorData = availableColorStocks.find(c => c.id === selectedColor);
        const colorName = selectedColorData?.name || "this color";

        message = `${t("Only")} ${maxQty} ${t("items available for")}${t("color")} ${colorName}`;
      } else {
        message = `${t("Only")} ${maxQty} ${t("items available in stock")}`;
      }

      setStockAlertMessage(message);
      setShowStockAlert(true);
      setTimeout(() => {
        setShowStockAlert(false);
        setStockAlertMessage("");
      }, 3000);
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleAddToCartClick = async () => {
    // Check if selection is valid
    if (!isSelectionValid()) {
      // Check what's missing and set appropriate message
      if (product?.bottleVariants && product.bottleVariants.length > 0) {
        const currentVariant = product.bottleVariants[selectedBottleVariantIndex];
        if (currentVariant?.capacityVariants && currentVariant.capacityVariants.length > 0 && !selectedCapacity) {
          setValidationError("Please select a capacity");
          Swal.fire({
            icon: 'warning',
            title: 'Selection Required',
            text: 'Please select a capacity option before adding to cart.',
            confirmButtonColor: '#3085d6',
          });
          return;
        }
      }

      if (availableColorStocks.length > 0 && !selectedColor) {
        setColorError(true);
        Swal.fire({
          icon: 'warning',
          title: 'Selection Required',
          text: 'Please select a color before adding to cart.',
          confirmButtonColor: '#3085d6',
        });
        return;
      }

      // If no specific validation error, show general message
      if (!isSelectionValid()) {
        Swal.fire({
          icon: 'warning',
          title: 'Selection Required',
          text: 'Please complete all selections before adding to cart.',
          confirmButtonColor: '#3085d6',
        });
        return;
      }

      return;
    }

    if (availableColorStocks.length > 0 && !selectedColor) {
      setColorError(true);
      Swal.fire({
        icon: 'warning',
        title: 'Color Required',
        text: 'Please select a color before adding to cart.',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    const selectedColorData = availableColorStocks.find(c => c.id === selectedColor);
    const capacityData = getSelectedCapacityData();
    const bottleData = getBottleVariantData();

    // Use product price as fallback if no valid variant data
    let currentPrice = product.price || 0;
    let currentStock = product.Item || 0;
    let displayTitle = product.title;
    let selectedBottleVariantData = null;

    // Override with capacity price if selected
    if (capacityData && capacityData.price) {
      currentPrice = capacityData.price;
      currentStock = capacityData.stock || product.Item || 0;
      displayTitle = `${product.title} (${capacityData.name})`;
    }
    // Override with bottle data if available and has valid price
    else if (bottleData && bottleData.finalPrice > 0) {
      currentPrice = bottleData.finalPrice;
      currentStock = bottleData.variant.stock > 0 ? bottleData.variant.stock : (product.Item || 0);
      displayTitle = `${product.title} (${bottleData.variant.bottleSize || 0}ml)`;
      selectedBottleVariantData = {
        bottleSize: bottleData.variant.bottleSize,
        sellingPrice: bottleData.sellingPrice,
        finalPrice: bottleData.finalPrice,
        discountValue: bottleData.discountValue,
        discountType: bottleData.discountType,
        hasDiscount: bottleData.hasDiscount
      };
    }

    // Check if this exact variant is already in the cart
    const isVariantInCart = cart.some(cartItem => {
      const cartProduct = cartItem.product;
      if (cartProduct?._id !== product?._id) return false;

      // Check color match
      if (selectedColor) {
        if (cartProduct?.selectedColor?.id !== selectedColor) return false;
      } else {
        if (cartProduct?.selectedColor) return false;
      }

      // Check capacity match (if capacity is selected)
      if (capacityData) {
        const cartCapacityName = cartProduct?.selectedCapacity?.name ||
          cartProduct?.selectedCapacity?.capacity;
        if (cartCapacityName !== capacityData.name) return false;
      }

      // Check bottle variant match
      if (selectedBottleVariantData) {
        const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;
        if (cartBottleSize !== selectedBottleVariantData.bottleSize) return false;
      }

      return true;
    });

    if (isVariantInCart) {
      // Build variant name only if there's a valid variant
      let variantName = '';
      if (capacityData) {
        variantName = capacityData.name;
      } else if (selectedBottleVariantData) {
        variantName = `${selectedBottleVariantData.bottleSize}ml`;
      }

      // Only show parentheses if there's a variant name
      const displayText = variantName ? `${product.title} (${variantName}) is already in your cart.` : `${product.title} is already in your cart.`;

      Swal.fire({
        icon: 'info',
        title: 'Already in Cart',
        text: displayText,
        confirmButtonColor: '#3498db',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    // For bottle variants, we need to preserve the discount information for display
    let discountValue = 0;
    let originalPrice = product.price;

    if (capacityData) {
      // Capacity selected - no discount
      discountValue = 0;
      originalPrice = capacityData.price;
    } else if (bottleData && bottleData.hasDiscount) {
      // Bottle variant with discount - preserve discount info
      discountValue = bottleData.discountValue;
      originalPrice = bottleData.sellingPrice;
    } else if (bottleData) {
      // Bottle variant without discount
      discountValue = 0;
      originalPrice = bottleData.sellingPrice;
    } else {
      // No variant - use product discount
      discountValue = product.discount || 0;
      originalPrice = product.price;
    }

    // Build the complete product object
    const completeProduct = {
      ...product,
      quantity: quantity,
      _id: product._id,
      title: product.title,
      price: currentPrice,
      originalPrice: originalPrice,
      discount: discountValue,
      discountType: bottleData?.discountType || 'percentage',
      images: product.images || [product.image],
      image: product.images?.[selectedImageIndex] || selectedImage,
      Item: currentStock,
      description: product.description,
      selectedColor: selectedColorData ? {
        id: selectedColorData.id,
        name: selectedColorData.name,
        hexCode: selectedColorData.hexCode,
        quantity: selectedColorData.quantity
      } : null,
      selectedCapacity: capacityData,
      selectedBottleVariant: selectedBottleVariantData,
      displayTitle: displayTitle
    };

    // Show loading state and add to cart
    try {
      await onAddToCart(completeProduct, quantity);
    } catch (error) {
      console.error('Error adding to cart:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add product to cart. Please try again.',
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleColorSelect = (colorId, colorIndex) => {
    const colorData = availableColorStocks.find(c => c.id === colorId);
    if (!colorData || colorData.quantity <= 0) return;

    setSelectedColor(colorId);
    setColorError(false);

    if (product?.images?.[colorIndex]) {
      setSelectedImage(product.images[colorIndex]);
      setSelectedImageIndex(colorIndex);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        className="modal-screenphone"
        style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Product Detail")}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <div className="d-flex product">
            {/* Left Side - Image Gallery */}
            <div className="flex-shrink-0 position-relative text-center">
              <div className="">
                <img
                  src={selectedImage}
                  alt={product.title}
                  className="img-fluid detail-product-image-size border rounded-4 mb-3"
                  style={{
                    maxHeight: "380px",
                    objectFit: "contain",
                    transition: "all 0.3s ease-in-out",
                  }}
                  onError={(e) => {
                    e.target.src = product.image || "/images/placeholder-image.jpg";
                  }}
                />
                {product.discount > 0 && (
                  <span className="position-absolute px-2 ms-3 mt-3 rounded-5 text-white btn-color-dis start-0 top-0">
                    -{product.discount}%
                  </span>
                )}
              </div>
              <div>
                {product.images && (
                  <div
                    className="d-flex justify-content-start align-items-center gap-2"
                    style={{
                      maxWidth: "300px",
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                      padding: "6px",
                      scrollbarWidth: "thin",
                    }}
                  >
                    {(Array.isArray(product.images) ? product.images : [product.images || product.image])
                      .filter(img => img)
                      .map((img, index) => {
                        const shouldBeClickable = availableColorStocks.length === 0
                          ? product.Item > 0
                          : !isImageOutOfStock(index);

                        return (
                          <img
                            key={index}
                            src={img}
                            alt={`Thumbnail ${index}`}
                            onClick={() => {
                              if (!shouldBeClickable) return;
                              setSelectedImage(img);
                              setSelectedImageIndex(index);
                              syncColorByImageIndex(index);
                            }}
                            className={`border border-2 rounded-1 shadow-sm ${selectedImage === img ? "border-dark" : ""}`}
                            style={{
                              width: "60px",
                              height: "60px",
                              cursor: shouldBeClickable ? "pointer" : "not-allowed",
                              objectFit: "cover",
                              opacity: shouldBeClickable ? 1 : 0.4,
                              filter: shouldBeClickable ? "none" : "grayscale(100%)",
                            }}
                            onMouseEnter={(e) => {
                              if (shouldBeClickable) {
                                e.currentTarget.style.transform = "scale(1.1)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        );
                      })}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Product Info */}
            <div className="flex-grow-1 ms-4 mt-4">
              <h5 className="mt-3 mt-md-0">{product.title}</h5>

              {product.linkUrl && (
                <div className="mb-2">
                  <Button
                    variant="link"
                    className="d-inline-flex align-items-center gap-2 p-0"
                    onClick={() => {
                      setVideoUrl(product.linkUrl);
                      setShowVideoModal(true);
                    }}
                    style={{ textDecoration: "none" }}
                  >
                    <FiVideo style={{ marginRight: "4px" }} />
                    {t("Link Video Detail")}
                  </Button>
                </div>
              )}

              <p>{product.description}</p>

              {/* Bottle Variant Selection Section - Only show if there's valid data */}
              {product?.bottleVariants && product.bottleVariants.length > 0 && (() => {
                // Check if any bottle variant has valid data
                const hasValidBottleData = product.bottleVariants.some(variant => {
                  const hasBottleSize = variant.bottleSize && parseFloat(variant.bottleSize) > 0;
                  const hasSellingPrice = variant.sellingPrice && parseFloat(variant.sellingPrice) > 0;
                  const hasStock = variant.stock && parseFloat(variant.stock) > 0;
                  const hasValidCapacityVariants = variant.capacityVariants &&
                    variant.capacityVariants.some(c =>
                      c.capacity && c.capacity.trim() !== '' &&
                      c.price && parseFloat(c.price) > 0
                    );
                  return hasBottleSize || hasSellingPrice || hasStock || hasValidCapacityVariants;
                });

                // If no valid bottle data, skip rendering the whole section
                if (!hasValidBottleData) {
                  return null;
                }

                return (
                  <div className="mt-3">
                    <Form.Label className="fw-bold">
                      {t("Select Bottle Size")}
                    </Form.Label>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {product.bottleVariants.map((variant, index) => {
                        // Skip rendering if this specific variant has no data
                        const hasData = variant.bottleSize && parseFloat(variant.bottleSize) > 0 ||
                          variant.sellingPrice && parseFloat(variant.sellingPrice) > 0 ||
                          variant.stock && parseFloat(variant.stock) > 0 ||
                          (variant.capacityVariants && variant.capacityVariants.some(c =>
                            c.capacity && c.capacity.trim() !== '' &&
                            c.price && parseFloat(c.price) > 0
                          ));

                        // If this variant has no data, skip it
                        if (!hasData) return null;

                        const isSelected = selectedBottleVariantIndex === index;
                        // Check if bottle variant has stock OR main product has stock
                        const variantStock = variant.stock || 0;
                        const mainStock = product.Item || 0;
                        const effectiveStock = variantStock > 0 ? variantStock : mainStock;
                        const isOutOfStock = effectiveStock <= 0;
                        const usingMainStock = variantStock === 0 && mainStock > 0;

                        const bottleSize = variant.bottleSize || 0;
                        const variantPrice = parseFloat(variant.sellingPrice) || 0;
                        const discountValue = parseFloat(variant.discountValue) || 0;
                        let finalPrice = variantPrice;

                        if (discountValue > 0) {
                          if (variant.discountType === 'percentage') {
                            finalPrice = variantPrice - (variantPrice * discountValue / 100);
                          } else {
                            finalPrice = variantPrice - discountValue;
                          }
                        }

                        const hasOptions = variant.capacityVariants && variant.capacityVariants.length > 0;

                        return (
                          <Button
                            key={index}
                            variant={isSelected ? "primary" : "outline-secondary"}
                            className={`d-flex align-items-center gap-2 ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                            onClick={() => !isOutOfStock && handleBottleVariantChange(index)}
                            style={{
                              borderRadius: '20px',
                              padding: '8px 20px',
                              borderColor: isSelected ? '#151b20' : '#dee2e6',
                              backgroundColor: isSelected ? '#141415' : 'transparent',
                              color: isSelected ? '#fff' : '#000',
                              position: 'relative',
                              cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            title={isOutOfStock ? t('Out of stock') : `${bottleSize}ml bottle${usingMainStock ? ` (${mainStock} units available)` : ''}`}
                          >
                            <span className="fw-bold" style={{ color: isSelected ? '#fff' : '#000' }}>
                              {bottleSize}ml
                            </span>
                            {isOutOfStock ? (
                              <span className="ms-1 small text-danger">({t('sold_out')})</span>
                            ) : (
                              <>
                                <span className="ms-1" style={{
                                  color: isSelected ? '#ffffff' : '#6c757d',
                                  fontWeight: isSelected ? '500' : '400'
                                }}>
                                  {(() => {
                                    const selectedCapacityData = getSelectedCapacityData();
                                    if (selectedCapacityData && selectedCapacityData.price) {
                                      return `$${selectedCapacityData.price.toFixed(2)}`;
                                    }
                                    return `$${finalPrice.toFixed(2)}`;
                                  })()}
                                </span>
                                {usingMainStock && (
                                  <span className="ms-1 badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                                    {mainStock} avail.
                                  </span>
                                )}
                                {(() => {
                                  const selectedCapacityData = getSelectedCapacityData();
                                  const hasSelectedCapacity = selectedCapacityData && selectedCapacityData.price;

                                  return (
                                    <>
                                      {hasOptions && (
                                        <span className="ms-1 small" style={{
                                          color: isSelected ? '#ffffff' : '#6c757d',
                                          fontWeight: isSelected ? '400' : '400'
                                        }}>
                                          ({hasSelectedCapacity ? 1 : variant.capacityVariants.length} {t("options")})
                                        </span>
                                      )}
                                      {(() => {
                                        const showDiscount = !hasSelectedCapacity && discountValue > 0;
                                        return showDiscount && (
                                          <span className="ms-1 badge bg-success" style={{
                                            color: '#fff',
                                            fontWeight: '500'
                                          }}>
                                            -{variant.discountType === 'percentage' ? `${discountValue}%` : `$${discountValue}`}
                                          </span>
                                        );
                                      })()}
                                    </>
                                  );
                                })()}
                              </>
                            )}
                            {isSelected && !isOutOfStock && (
                              <span className="ms-1" style={{ color: '#fff' }}>✓</span>
                            )}
                          </Button>
                        );
                      })}
                    </div>

                    {/* Show capacity options for selected bottle */}
                    {selectedBottleVariantIndex !== null &&
                      product.bottleVariants[selectedBottleVariantIndex] &&
                      product.bottleVariants[selectedBottleVariantIndex].capacityVariants &&
                      product.bottleVariants[selectedBottleVariantIndex].capacityVariants.length > 0 && (
                        <div className="mt-3">
                          <Form.Label className="fw-bold" style={{ fontSize: '0.95rem', color: '#2d3748' }}>
                            {t("Select Capacity ml")}
                          </Form.Label>

                          {/* Mobile Popup/Modal Selection - Multiple Select */}
                          <div className="d-block d-sm-none">
                            {/* Trigger Button - Shows selected count or placeholder */}
                            <div
                              className="d-flex align-items-center justify-content-between p-3 rounded-3"
                              onClick={() => {
                                // Get capacity options
                                const capacityOptions = [...product.bottleVariants[selectedBottleVariantIndex].capacityVariants]
                                  .sort((a, b) => {
                                    if (a.capacity === 'Full Box') return -1;
                                    if (b.capacity === 'Full Box') return 1;
                                    return a.capacity.localeCompare(b.capacity);
                                  });

                                // Build HTML with inline onclick handlers
                                const optionsHtml = capacityOptions.map((item, idx) => {
                                  const itemPrice = parseFloat(item.price) || 0;
                                  const capacityName = item.capacity || '';
                                  const fullVariant = product.capacityVariants?.find(v => v.name === capacityName);
                                  const isOutOfStock = fullVariant ? (fullVariant.stock || 0) <= 0 : false;
                                  const capacityId = fullVariant?._id || `temp_${capacityName}`;
                                  const isSelected = selectedCapacity === capacityId;

                                  return `
                <div 
                  class="capacity-option-item ${isOutOfStock ? 'out-of-stock' : ''} ${isSelected ? 'selected' : ''}"
                  data-capacity-id="${capacityId}"
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 16px;
                    margin-bottom: 8px;
                    border-radius: 12px;
                    border: 2px solid ${isSelected ? '#667eea' : (isOutOfStock ? '#f0f0f0' : '#e2e8f0')};
                    background: ${isSelected ? '#f0f4ff' : (isOutOfStock ? '#fafafa' : '#ffffff')};
                    cursor: ${isOutOfStock ? 'not-allowed' : 'pointer'};
                    transition: all 0.2s ease;
                    opacity: ${isOutOfStock ? '0.5' : '1'};
                    ${isSelected ? 'box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);' : ''}
                  "
                  onclick="window.selectCapacityHandler('${capacityId}')"
                >
                  <div style="display: flex; align-items: center; gap: 12px;">
                    ${isSelected ? `
                      <span style="
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 24px;
                        height: 24px;
                        background: #667eea;
                        border-radius: 50%;
                        color: white;
                        font-size: 12px;
                        font-weight: bold;
                      ">✓</span>
                    ` : `
                      <span style="
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 24px;
                        height: 24px;
                        background: ${isOutOfStock ? '#f0f0f0' : '#e8ecf1'};
                        border-radius: 50%;
                        color: ${isOutOfStock ? '#ccc' : '#667eea'};
                        font-size: 12px;
                        font-weight: bold;
                      ">${idx + 1}</span>
                    `}
                    <div>
                      <div style="font-weight: 600; color: ${isOutOfStock ? '#999' : '#2d3748'}; font-size: 1rem;">
                        ${capacityName}
                      </div>
                      <div style="font-size: 0.85rem; color: ${isOutOfStock ? '#bbb' : '#667eea'};">
                        $${itemPrice.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div style="text-align: right;">
                    ${isOutOfStock ? `
                      <span style="font-size: 0.75rem; color: #e53e3e; background: #fff5f5; padding: 2px 10px; border-radius: 20px;">
                        Out of Stock
                      </span>
                    ` : `
                      ${isSelected ? `
                        <span style="font-size: 0.75rem; color: #667eea; background: #ebf4ff; padding: 2px 10px; border-radius: 20px;">
                          Selected
                        </span>
                      ` : `
                        <span style="font-size: 0.75rem; color: #a0aec0;">Tap to select</span>
                      `}
                    `}
                  </div>
                </div>
              `;
                                }).join('');

                                // Define the handler in the window object
                                window.selectCapacityHandler = (capacityId) => {
                                  // Find the capacity item
                                  const capacityItem = capacityOptions.find(item => {
                                    const fullVariant = product.capacityVariants?.find(v => v.name === item.capacity);
                                    const id = fullVariant?._id || `temp_${item.capacity}`;
                                    return id === capacityId;
                                  });

                                  if (capacityItem) {
                                    const fullVariant = product.capacityVariants?.find(v => v.name === capacityItem.capacity);
                                    const id = fullVariant?._id || `temp_${capacityItem.capacity}`;

                                    // Toggle selection (allow multiple)
                                    if (selectedCapacity === id) {
                                      setSelectedCapacity(null);
                                      setQuantity(1);
                                    } else {
                                      setSelectedCapacity(id);
                                      setQuantity(1);
                                    }
                                  }

                                  // Close the Swal
                                  Swal.close();
                                };

                                Swal.fire({
                                  title: 'Select Capacity',
                                  html: `
                <div style="text-align: left; padding: 8px 0; max-height: 400px; overflow-y: auto;">
                  ${optionsHtml}
                </div>
              `,
                                  showCancelButton: true,
                                  cancelButtonText: 'Cancel',
                                  confirmButtonText: 'Done',
                                  confirmButtonColor: '#667eea',
                                  cancelButtonColor: '#e2e8f0',
                                  background: '#ffffff',
                                  customClass: {
                                    popup: 'modern-capacity-popup',
                                    cancelButton: 'modern-capacity-cancel-btn',
                                    confirmButton: 'modern-capacity-confirm-btn'
                                  },
                                  width: '92%',
                                  padding: '20px',
                                  backdrop: 'rgba(0, 0, 0, 0.3)',
                                  willOpen: () => {
                                    const style = document.createElement('style');
                                    style.textContent = `
                  .modern-capacity-popup {
                    border-radius: 20px !important;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
                    max-height: 80vh !important;
                  }
                  .modern-capacity-popup .swal2-title {
                    color: #2d3748;
                    font-weight: 700;
                    font-size: 1.25rem;
                    margin-bottom: 8px;
                  }
                  .modern-capacity-popup .swal2-html-container {
                    padding: 0 !important;
                    margin: 0 !important;
                  }
                  .modern-capacity-cancel-btn {
                    border-radius: 12px !important;
                    padding: 10px 24px !important;
                    font-weight: 600 !important;
                    color: #4a5568 !important;
                    border: 2px solid #e2e8f0 !important;
                    background: white !important;
                    transition: all 0.2s ease !important;
                  }
                  .modern-capacity-cancel-btn:hover {
                    background: #f7fafc !important;
                    border-color: #cbd5e0 !important;
                  }
                  .modern-capacity-confirm-btn {
                    border-radius: 12px !important;
                    padding: 10px 24px !important;
                    font-weight: 600 !important;
                    background: #667eea !important;
                    border: none !important;
                    transition: all 0.2s ease !important;
                  }
                  .modern-capacity-confirm-btn:hover {
                    background: #5a6fd6 !important;
                    transform: scale(1.02);
                  }
                  .capacity-option-item {
                    animation: slideIn 0.3s ease forwards;
                    opacity: 0;
                    transform: translateY(10px);
                  }
                  .capacity-option-item:nth-child(1) { animation-delay: 0.05s; }
                  .capacity-option-item:nth-child(2) { animation-delay: 0.10s; }
                  .capacity-option-item:nth-child(3) { animation-delay: 0.15s; }
                  .capacity-option-item:nth-child(4) { animation-delay: 0.20s; }
                  .capacity-option-item:nth-child(5) { animation-delay: 0.25s; }
                  .capacity-option-item:nth-child(6) { animation-delay: 0.30s; }
                  
                  @keyframes slideIn {
                    from {
                      opacity: 0;
                      transform: translateY(10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `;
                                    document.head.appendChild(style);
                                  }
                                }).then((result) => {
                                  if (result.dismiss === Swal.DismissReason.cancel) {
                                    // User cancelled, do nothing
                                  }
                                  // Clean up the handler
                                  delete window.selectCapacityHandler;
                                });
                              }}
                              style={{
                                border: '2px solid #e2e8f0',
                                borderRadius: '14px',
                                backgroundColor: '#f7fafc',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                minHeight: '56px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.backgroundColor = '#ffffff';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.backgroundColor = '#f7fafc';
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '32px',
                                    background: selectedCapacity ? '#667eea' : '#e8ecf1',
                                    borderRadius: '50%',
                                    color: selectedCapacity ? 'white' : '#a0aec0',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {selectedCapacity ? '✓' : '?'}
                                </span>
                                <div>
                                  {selectedCapacity ? (
                                    <>
                                      <div style={{ fontSize: '0.7rem', color: '#667eea', fontWeight: '500' }}>
                                        SELECTED
                                      </div>
                                      <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>
                                        {(() => {
                                          if (typeof selectedCapacity === 'string' && selectedCapacity.startsWith('temp_')) {
                                            const tempName = selectedCapacity.replace('temp_', '');
                                            const bottleData = getBottleVariantData();
                                            const capItem = bottleData?.variant.capacityVariants?.find(c => c.capacity === tempName);
                                            if (capItem) {
                                              return `${capItem.capacity} • $${capItem.price}`;
                                            }
                                          }
                                          const selected = product.capacityVariants?.find(v => v._id === selectedCapacity);
                                          if (selected) {
                                            return `${selected.name} • $${selected.price}`;
                                          }
                                          return null;
                                        })()}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div style={{ fontSize: '0.7rem', color: '#a0aec0', fontWeight: '500' }}>
                                        TAP TO SELECT
                                      </div>
                                      <div style={{ color: '#a0aec0', fontWeight: '400', fontSize: '0.95rem' }}>
                                        Choose capacity...
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div style={{ color: '#a0aec0' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </div>
                            </div>

                            {/* Selected capacity indicator with X button for deselect */}
                            {selectedCapacity && (
                              <div className="mt-2">
                                <div
                                  className="d-flex align-items-center justify-content-between p-2 rounded-3"
                                  style={{
                                    background: '#f0f4ff',
                                    border: '1px solid #d4e2fc',
                                    borderRadius: '10px'
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ fontSize: '0.8rem', color: '#4a5568' }}>
                                      ✓ {t("Selected")}:
                                    </span>
                                    <span className="fw-semibold" style={{ fontSize: '0.85rem', color: '#2d3748' }}>
                                      {(() => {
                                        if (typeof selectedCapacity === 'string' && selectedCapacity.startsWith('temp_')) {
                                          const tempName = selectedCapacity.replace('temp_', '');
                                          const bottleData = getBottleVariantData();
                                          const capItem = bottleData?.variant.capacityVariants?.find(c => c.capacity === tempName);
                                          if (capItem) {
                                            return `${capItem.capacity} • $${capItem.price}`;
                                          }
                                        }
                                        const selected = product.capacityVariants?.find(v => v._id === selectedCapacity);
                                        if (selected) {
                                          return `${selected.name} • $${selected.price}`;
                                        }
                                        return null;
                                      })()}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      setSelectedCapacity(null);
                                      setQuantity(1);
                                    }}
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      color: '#e53e3e',
                                      fontSize: '1.1rem',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      padding: '0 6px',
                                      borderRadius: '50%',
                                      width: '28px',
                                      height: '28px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#fee2e2';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'transparent';
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Desktop scrollable buttons - visible on larger screens */}
                          <div className="d-none d-sm-flex gap-2 mt-2 capacity-scroll-container" style={{ overflowX: 'auto', flexWrap: 'nowrap', paddingBottom: '8px' }}>
                            {[...product.bottleVariants[selectedBottleVariantIndex].capacityVariants]
                              .sort((a, b) => {
                                if (a.capacity === 'Full Box') return -1;
                                if (b.capacity === 'Full Box') return 1;
                                return a.capacity.localeCompare(b.capacity);
                              })
                              .map((item, idx) => {
                                const itemPrice = parseFloat(item.price) || 0;
                                const capacityName = item.capacity || '';

                                const fullVariant = product.capacityVariants?.find(v => v.name === capacityName);
                                const isOutOfStock = fullVariant ? (fullVariant.stock || 0) <= 0 : false;
                                const capacityId = fullVariant?._id || `temp_${capacityName}`;
                                const isCapacitySelected = selectedCapacity === capacityId;

                                const isInCart = cart.some(cartItem => {
                                  const cartProduct = cartItem.product;
                                  if (cartProduct?._id !== product?._id) return false;
                                  if (selectedColor) {
                                    if (cartProduct?.selectedColor?.id !== selectedColor) return false;
                                  } else {
                                    if (cartProduct?.selectedColor) return false;
                                  }
                                  const cartCapacityName = cartProduct?.selectedCapacity?.name ||
                                    cartProduct?.selectedCapacity?.capacity;
                                  const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;
                                  const currentBottleSize = product.bottleVariants[selectedBottleVariantIndex]?.bottleSize;
                                  return cartCapacityName === capacityName && cartBottleSize === currentBottleSize;
                                });

                                return (
                                  <Button
                                    key={idx}
                                    variant={isCapacitySelected ? "primary" : "outline-secondary"}
                                    className={`d-flex align-items-center gap-2 flex-shrink-0 ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                                    onClick={() => {
                                      if (isOutOfStock) {
                                        Swal.fire({
                                          icon: 'warning',
                                          title: 'Out of Stock',
                                          text: 'This capacity is currently out of stock.',
                                          confirmButtonColor: '#3085d6',
                                        });
                                        return;
                                      }
                                      const capacityId = fullVariant?._id || `temp_${capacityName}`;
                                      if (isCapacitySelected) {
                                        setSelectedCapacity(null);
                                        setQuantity(1);
                                      } else {
                                        setSelectedCapacity(capacityId);
                                        setQuantity(1);
                                      }
                                    }}
                                    style={{
                                      borderRadius: '24px',
                                      padding: '8px 20px',
                                      fontSize: '0.9rem',
                                      borderColor: isInCart ? '#28a745' : (isCapacitySelected ? '#667eea' : '#e2e8f0'),
                                      backgroundColor: isInCart ? '#d4edda' : (isCapacitySelected ? '#667eea' : 'transparent'),
                                      color: isInCart ? '#155724' : (isCapacitySelected ? '#ffffff' : '#2d3748'),
                                      position: 'relative',
                                      cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                                      transition: 'all 0.2s ease',
                                      boxShadow: isCapacitySelected ? '0 4px 12px rgba(102, 126, 234, 0.25)' : 'none',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                      fontWeight: isCapacitySelected ? '600' : '400'
                                    }}
                                    title={isOutOfStock ? t('Out of stock') : `${capacityName} - $${itemPrice.toFixed(2)}`}
                                  >
                                    <span className="fw-medium">{capacityName}</span>
                                    <span className="ms-1" style={{ color: isInCart ? '#155724' : (isCapacitySelected ? 'rgba(255,255,255,0.8)' : '#6c757d') }}>
                                      (${itemPrice.toFixed(2)})
                                    </span>
                                    {isInCart && (
                                      <span className="ms-1 text-success" style={{ fontSize: '0.7rem' }}>
                                        ✓ {t("in_cart_short")}
                                      </span>
                                    )}
                                    {isOutOfStock && (
                                      <span className="ms-1 small text-danger">({t('sold_out')})</span>
                                    )}
                                    {isCapacitySelected && !isOutOfStock && !isInCart && (
                                      <span className="ms-1">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M13.3334 4L5.33342 12L2.66675 9.33333" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      </span>
                                    )}
                                    {isCapacitySelected && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedCapacity(null);
                                          setQuantity(1);
                                        }}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: isCapacitySelected ? 'rgba(255,255,255,0.7)' : '#e53e3e',
                                          fontSize: '1rem',
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          padding: '0 4px',
                                          marginLeft: '4px',
                                          borderRadius: '50%',
                                          width: '20px',
                                          height: '20px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (isCapacitySelected) {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'transparent';
                                        }}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </Button>
                                );
                              })}
                          </div>
                        </div>
                      )}

                  </div>
                );
              })()}
              {/* Capacity (ML) Selection Section - For products without bottle variants */}
              {!product?.bottleVariants?.length && product?.capacityVariants && product.capacityVariants.length > 0 && (
                <div className="mt-3">
                  <Form.Label className="fw-bold">
                    {t("Select Size")}
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {product.capacityVariants.map((variant) => {
                      const isSelected = selectedCapacity === variant._id;
                      const isOutOfStock = variant.stock <= 0;

                      return (
                        <Button
                          key={variant._id}
                          variant={isSelected ? "primary" : "outline-secondary"}
                          className={`d-flex align-items-center gap-2 ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                          onClick={() => !isOutOfStock && handleCapacitySelect(variant._id)}
                          style={{
                            borderRadius: '20px',
                            padding: '8px 20px',
                            borderColor: isSelected ? '#3498db' : '#dee2e6',
                            backgroundColor: isSelected ? '#3498db' : 'transparent',
                            color: isSelected ? '#fff' : '#000',
                            position: 'relative',
                            cursor: isOutOfStock ? 'not-allowed' : 'pointer'
                          }}
                          title={isOutOfStock ? t('Out of stock') : `${variant.name} - $${variant.price} (${variant.stock} available)`}
                        >
                          <span className="fw-bold">{variant.name}</span>
                          <span className="text-muted ms-1" style={{ color: isSelected ? '#fff' : '#6c757d' }}>
                            (${variant.price})
                          </span>
                          {isOutOfStock && (
                            <span className="ms-1 small text-danger">({t('sold_out')})</span>
                          )}
                          {isSelected && !isOutOfStock && (
                            <span className="ms-1">✓</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>

                  {/* Show selected capacity info or default message */}
                  <div className="mt-2 text-muted small">
                    {selectedCapacity ? (
                      (() => {
                        const selected = product.capacityVariants.find(v => v._id === selectedCapacity);
                        if (selected) {
                          return (
                            <span>
                              {t("Selected")}: <strong>{selected.name}</strong> -
                              ${selected.price}
                              {selected.stock > 0 ? ` | ${selected.stock} ${t("available")}` : ` | ${t("out_of_stock")}`}
                              <span className="ms-2 text-primary" style={{ cursor: 'pointer' }} onClick={() => setSelectedCapacity(null)}>
                                <small>({t("deselect")})</small>
                              </span>
                            </span>
                          );
                        }
                        return null;
                      })()
                    ) : (
                      <span className="text-muted">
                        <i>{t("Please select a size")}</i>
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* Capacity (ML) Selection Section */}
              {product?.capacityVariants && product.capacityVariants.length > 0 && (
                <div className="mt-3">
                  <Form.Label className="fw-bold">
                    {t("Select Size")}
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {product.capacityVariants.map((variant) => {
                      const isSelected = selectedCapacity === variant._id;
                      const isOutOfStock = variant.stock <= 0;

                      return (
                        <Button
                          key={variant._id}
                          variant={isSelected ? "primary" : "outline-secondary"}
                          className={`d-flex align-items-center gap-2 ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                          onClick={() => !isOutOfStock && handleCapacitySelect(variant._id)}
                          style={{
                            borderRadius: '20px',
                            padding: '8px 20px',
                            borderColor: isSelected ? '#3498db' : '#dee2e6',
                            backgroundColor: isSelected ? '#3498db' : 'transparent',
                            color: isSelected ? '#fff' : '#000',
                            position: 'relative',
                            cursor: isOutOfStock ? 'not-allowed' : 'pointer'
                          }}
                          title={isOutOfStock ? t('Out of stock') : `${variant.name} - $${variant.price} (${variant.stock} available)`}
                        >
                          <span className="fw-bold">{variant.name}</span>
                          <span className="text-muted ms-1" style={{ color: isSelected ? '#fff' : '#6c757d' }}>
                            (${variant.price})
                          </span>
                          {isOutOfStock && (
                            <span className="ms-1 small text-danger">({t('sold_out')})</span>
                          )}
                          {isSelected && !isOutOfStock && (
                            <span className="ms-1">✓</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>

                  {/* Show selected capacity info or default message */}
                  <div className="mt-2 text-muted small">
                    {selectedCapacity ? (
                      (() => {
                        const selected = product.capacityVariants.find(v => v._id === selectedCapacity);
                        if (selected) {
                          return (
                            <span>
                              {t("Selected")}: <strong>{selected.name}</strong> -
                              ${selected.price}
                              {selected.stock > 0 ? ` | ${selected.stock} ${t("available")}` : ` | ${t("out_of_stock")}`}
                              <span className="ms-2 text-primary" style={{ cursor: 'pointer' }} onClick={() => setSelectedCapacity(null)}>
                                <small>({t("deselect")})</small>
                              </span>
                            </span>
                          );
                        }
                        return null;
                      })()
                    ) : (
                      <span className="text-muted">
                        <i>{t("Please select a size")}</i>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Color Selection Section */}
              {availableColorStocks.length > 0 ? (
                <div className="mt-3">
                  <Form.Label className="fw-bold">
                    {t("Select Color")} <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {availableColorStocks.map((color, index) => {
                      const isSelected = selectedColor === color.id;
                      const stockQuantity = color.quantity ?? 0;
                      const isOutOfStock = stockQuantity <= 0;

                      return (
                        <Button
                          key={color.id}
                          variant={isSelected ? "primary" : "outline-secondary"}
                          className={`d-flex align-items-center gap-2 ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                          onClick={() => handleColorSelect(color.id, index)}
                          style={{
                            borderRadius: '20px',
                            padding: '8px 16px',
                            borderColor: isSelected ? color.hexCode : '#dee2e6',
                            backgroundColor: isSelected ? color.hexCode : 'transparent',
                            color: isSelected ? (getContrastColor(color.hexCode) === 'dark' ? '#000' : '#fff') : '#000',
                            position: 'relative'
                          }}
                          title={isOutOfStock ? t('Out of stock') : `${color.name} (${stockQuantity} available)`}
                        >
                          <div
                            className="rounded-circle"
                            style={{
                              width: '15px',
                              height: '15px',
                              backgroundColor: color.hexCode,
                              border: '1px solid #ccc'
                            }}
                          />
                          <span>
                            {color.name}
                          </span>
                          {isOutOfStock && (
                            <span className="ms-1 small text-danger">(Sold out)</span>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                  {colorError && (
                    <Alert variant="danger" className="mt-2 py-2">
                      {t("Please choose your color")}
                    </Alert>
                  )}
                </div>
              ) : (
                <div></div>
              )}

              <h5 className="text-start fw-medium border-0 price-color d-flex justify-content-start align-items-center gap-2 mt-4 mt-md-3">
                {(() => {
                  const bottleData = getBottleVariantData();
                  const capacityData = getSelectedCapacityData();

                  // If capacity is selected, show its price ONLY (no discount, no bottle price)
                  if (capacityData) {
                    return <span>${capacityData.price.toFixed(2)}</span>;
                  }

                  // If bottle variant is selected with discount (and NO capacity selected)
                  if (bottleData && bottleData.hasDiscount) {
                    return (
                      <div className="d-flex align-items-center">
                        <span className="text-decoration-line-through text-muted me-2">
                          ${bottleData.sellingPrice.toFixed(2)}
                        </span>
                        <span className="text-primary fw-bold">
                          ${bottleData.finalPrice.toFixed(2)}
                        </span>
                        <span className="ms-2 badge bg-success">
                          -{bottleData.discountType === 'percentage'
                            ? `${bottleData.discountValue}%`
                            : `$${bottleData.discountValue}`}
                        </span>
                      </div>
                    );
                  }

                  // If bottle variant selected with no discount (and NO capacity selected)
                  if (bottleData) {
                    return <span>${bottleData.finalPrice.toFixed(2)}</span>;
                  }

                  // Show main product discount if no variant selected
                  if (product.discount > 0) {
                    return (
                      <div className="d-flex">
                        <span className="text-decoration-line-through text-muted me-2">
                          ${product.price.toFixed(2)}
                        </span>
                        <span>
                          ${(product.price * (1 - product.discount / 100)).toFixed(2)}
                        </span>
                      </div>
                    );
                  }

                  return <span>${product.price.toFixed(2)}</span>;
                })()}
              </h5>
              <div className="d-flex justify-content-between align-items-center mt-custom-price">
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-secondary"
                    className="px-3 py-1"
                    onClick={handleDecreaseQuantity}
                    disabled={quantity === 1}
                  >
                    -
                  </Button>
                  <span className="mx-3">{quantity}</span>
                  <Button
                    variant="outline-secondary"
                    className="px-3 py-1"
                    onClick={handleIncreaseQuantity}
                  >
                    +
                  </Button>
                </div>
                <h5 className="mt-md-1 float-end total-price ms-custom">
                  {t("Total")}{" "}
                  <span className="price-color">${calculateTotalPrice()}</span>
                </h5>
              </div>

              {showStockAlert && (
                <Alert variant="warning" className="mt-2 mb-0 py-2">
                  {stockAlertMessage}
                </Alert>
              )}

              <div className="d-flex align-items-center justify-content-start mt-4 display-screenphone">
                <Button
                  variant=""
                  className={`btn-color btn-card-hover btn-phonescreen px-4 rounded-0 text-white mt-md-5 text-align-center w-md-50`}
                  onClick={handleAddToCartClick}
                  disabled={
                    !isSelectionValid() ||
                    !getCurrentStock() || getCurrentStock() <= 0 ||
                    (loadingItems && loadingItems[product._id]) ||
                    (() => {
                      // Get current selection
                      const capacityData = getSelectedCapacityData();
                      const capacityName = capacityData?.name || null;
                      const bottleData = getBottleVariantData();
                      const bottleSize = bottleData?.variant?.bottleSize || null;

                      // If no capacity and no bottle size, it's the base product
                      if (!capacityName && !bottleSize) return false;

                      // Check if EXACT same variant is in cart (capacity + bottle size + color)
                      return cart.some(cartItem => {
                        const cartProduct = cartItem.product;
                        if (cartProduct?._id !== product?._id) return false;

                        // Check color match
                        if (selectedColor && cartProduct?.selectedColor?.id !== selectedColor) return false;
                        if (!selectedColor && cartProduct?.selectedColor) return false;

                        const cartCapacityName = cartProduct?.selectedCapacity?.name ||
                          cartProduct?.selectedCapacity?.capacity;
                        const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;

                        // Check capacity match
                        const capacityMatch = (!capacityName && !cartCapacityName) ||
                          (capacityName && cartCapacityName === capacityName);

                        // Check bottle size match
                        const bottleMatch = (!bottleSize && !cartBottleSize) ||
                          (bottleSize && cartBottleSize === bottleSize);

                        return capacityMatch && bottleMatch;
                      });
                    })()
                  }
                  style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}
                >
                  {(() => {
                    // Use the SAME check for button text
                    if (!getCurrentStock() || getCurrentStock() <= 0) {
                      return t("sold_out");
                    }

                    if (loadingItems && loadingItems[product._id]) {
                      return (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          {t("adding")}
                        </>
                      );
                    }

                    // Check if EXACT same variant is in cart using the SAME inline check
                    const capacityData = getSelectedCapacityData();
                    const capacityName = capacityData?.name || null;
                    const bottleData = getBottleVariantData();
                    const bottleSize = bottleData?.variant?.bottleSize || null;

                    const isInCart = (() => {
                      if (!capacityName && !bottleSize) return false;

                      return cart.some(cartItem => {
                        const cartProduct = cartItem.product;
                        if (cartProduct?._id !== product?._id) return false;

                        if (selectedColor && cartProduct?.selectedColor?.id !== selectedColor) return false;
                        if (!selectedColor && cartProduct?.selectedColor) return false;

                        const cartCapacityName = cartProduct?.selectedCapacity?.name ||
                          cartProduct?.selectedCapacity?.capacity;
                        const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;

                        const capacityMatch = (!capacityName && !cartCapacityName) ||
                          (capacityName && cartCapacityName === capacityName);
                        const bottleMatch = (!bottleSize && !cartBottleSize) ||
                          (bottleSize && cartBottleSize === bottleSize);

                        return capacityMatch && bottleMatch;
                      });
                    })();

                    if (isInCart) {
                      return (
                        <>
                          <FaShoppingCart size={18} className="me-2" />
                          {t("in_cart_btn")}
                        </>
                      );
                    }

                    if (!isSelectionValid()) {
                      return (
                        <>
                          <FaCartPlus size={18} className="me-2" />
                          {t("Select Options")}
                        </>
                      );
                    }

                    return (
                      <>
                        <FaCartPlus size={18} className="me-2" />
                        {t("add_to_cart")}
                      </>
                    );
                  })()}
                </Button>

                <Button
                  variant=""
                  className="ms-md-4 btn-color-blue btn-card-hover px-5 rounded-0 text-white mt-md-5 text-align-center"
                  onClick={handleBuyNow}
                  disabled={
                    !isSelectionValid() ||
                    !getCurrentStock() || getCurrentStock() <= 0 ||
                    (() => {
                      const capacityData = getSelectedCapacityData();
                      const capacityName = capacityData?.name || null;
                      const bottleData = getBottleVariantData();
                      const bottleSize = bottleData?.variant?.bottleSize || null;

                      if (!capacityName && !bottleSize) return false;

                      return cart.some(cartItem => {
                        const cartProduct = cartItem.product;
                        if (cartProduct?._id !== product?._id) return false;
                        if (selectedColor && cartProduct?.selectedColor?.id !== selectedColor) return false;
                        if (!selectedColor && cartProduct?.selectedColor) return false;

                        const cartCapacityName = cartProduct?.selectedCapacity?.name ||
                          cartProduct?.selectedCapacity?.capacity;
                        const cartBottleSize = cartProduct?.selectedBottleVariant?.bottleSize;

                        const capacityMatch = (!capacityName && !cartCapacityName) ||
                          (capacityName && cartCapacityName === capacityName);
                        const bottleMatch = (!bottleSize && !cartBottleSize) ||
                          (bottleSize && cartBottleSize === bottleSize);

                        return capacityMatch && bottleMatch;
                      });
                    })()
                  }
                >
                  {t("Buy Now")}
                </Button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Video Modal */}
      <Modal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        size="xl"
        centered
        dialogClassName="video-modal-full"
        contentClassName="border-0"
      >
        <Modal.Body className="p-0" style={{ height: "75vh" }}>
          {videoUrl && (
            <iframe
              width="100%"
              height="100%"
              src={getEmbedUrl(videoUrl)}
              title="Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ borderRadius: 0, width: "100%", height: "100%" }}
            ></iframe>
          )}
        </Modal.Body>
      </Modal>

      {/* Required login */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
        <Modal.Body className="shadow-lg rounded-3">
          <CloseButton
            className="close-btn p-0 float-end"
            onClick={() => setShowLoginModal(false)}
          />
          <div className="d-flex justify-content-between align-items-center mt-3">
            <p className="pt-3">Login is required!</p>
            <Button
              variant=""
              className="bg-transparent border px-3 login-hover"
              onClick={() => {
                setShowLoginModal(false);
                navigate("/login");
                onHide();
              }}
            >
              Log In
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProductDetailModal;

// Correct with 1714 line code changes
